/**
 * useQuestions — question + group data hook backed by @v0y4ge/api-client.
 * No seed data in the UI. All data comes through the contract layer.
 * CRUD mutations call the typed client and reconcile local state from the
 * response. A failed write REJECTS and commits nothing (2026-07-25, scrub-audit
 * F6) — the previous fallback-to-a-local-record made failures indistinguishable
 * from successes; callers own the error state.
 */
import { t as i18nT } from "../../lib/i18n";
import { useState, useEffect } from "react";
import {
  listQuestions, createQuestion, patchQuestion, deleteQuestion, duplicateQuestion,
  listQuestionGroups, createQuestionGroup, patchQuestionGroup, deleteQuestionGroup, duplicateQuestionGroup,
} from "@v0y4ge/api-client";
import type { QTextRules } from "./text-validation";
import type { QFileConfig, QInfoConfig, QSelectConfig, QStepperConfig, QYesNoConfig, QNumberConfig, QDateConfig } from "./question-model";
import type { QuestionDto, QuestionGroupDto } from "@v0y4ge/api-contracts";
import { logUpsert, logDelete } from "../../lib/audit";
import { can, onTenantChange, onTenantSwitching } from "../../shell/access";

/* ── Field-type catalog (static config — not data) ──
 * ONE catalog, read by the editor picker, the library badges + type filter, the
 * group editor, surveys and the event-day renderer. `QFIELD_TYPES` is the FULL
 * set (it must resolve every type that exists in stored data, including retired
 * ones); `Q_PICKER_TYPES` is what an author may choose today. */
export const QFIELD_TYPES = [
  { id: "heading",  labelKey: "questions.useQuestions.sectionHeading", get label() { return i18nT("questions.useQuestions.sectionHeading", "Section heading"); }, icon: "heading", group: "Layout",  opts: false, branch: false, value: false, hint: "Static title / divider." },
  { id: "label",    labelKey: "questions.useQuestions.infoText", get label() { return i18nT("questions.useQuestions.infoText", "Info text"); },       icon: "text",    group: "Layout",  opts: false, branch: false, value: false, hint: "Guidance text — collects no answer." },
  { id: "short",    labelKey: "questions.useQuestions.shortText", get label() { return i18nT("questions.useQuestions.shortText", "Short text"); },      icon: "input",   group: "Text",    opts: false, branch: false, value: true,  hint: "Single-line entry." },
  { id: "long",     labelKey: "questions.useQuestions.longText", get label() { return i18nT("questions.useQuestions.longText", "Long text"); },       icon: "area",    group: "Text",    opts: false, branch: false, value: true,  hint: "Multi-line entry area." },
  { id: "number",   labelKey: "questions.useQuestions.number", get label() { return i18nT("questions.useQuestions.number", "Number"); },          icon: "hash",    group: "Text",    opts: false, branch: false, value: true,  hint: "Numeric entry." },
  { id: "radio",    labelKey: "questions.useQuestions.radioButtons", get label() { return i18nT("questions.useQuestions.radioButtons", "Radio buttons"); },   icon: "radio",   group: "Choice",  opts: true,  branch: true,  value: true,  hint: "Pick one — all options visible." },
  { id: "select",   labelKey: "questions.useQuestions.dropdown", get label() { return i18nT("questions.useQuestions.dropdown", "Dropdown"); },        icon: "caret",   group: "Choice",  opts: true,  branch: true,  value: true,  hint: "Pick one from a dropdown." },
  { id: "yesno",    labelKey: "questions.useQuestions.toggle", get label() { return i18nT("questions.useQuestions.toggle", "Toggle"); },        icon: "toggle",  group: "Choice",  opts: false, branch: true,  value: true,  hint: "On/off switch — a single yes-or-no state." },
  { id: "checks",   labelKey: "questions.useQuestions.checkboxGroup", get label() { return i18nT("questions.useQuestions.checkboxGroup", "Checkbox group"); },  icon: "checks",  group: "Choice",  opts: true,  branch: true, value: true,  hint: "Pick any number of options." },
  // RETIRED as a choice (2026-07-31): Dropdown + "allow several" replaces it.
  // Kept in the catalog because stored questions still carry `type: "multi"` and
  // must render with the right name until they are re-saved (the editor migrates
  // one to `select` on open — see normalizeQuestion).
  { id: "multi",    labelKey: "questions.useQuestions.multiSelect", get label() { return i18nT("questions.useQuestions.multiSelect", "Multi-select"); },    icon: "multi",   group: "Choice",  opts: true,  branch: false, value: true,  hint: "Pick several from a dropdown." },
  // RETIRED as a choice (2026-10-07, operator: date only). Still rendered and re-savable.
  { id: "datetime", labelKey: "questions.useQuestions.dateTime", get label() { return i18nT("questions.useQuestions.dateTime", "Date & time"); },     icon: "calendar",group: "Special", opts: false, branch: false, value: true,  hint: "Date/time picker." },
  { id: "date",     labelKey: "questions.useQuestions.date", get label() { return i18nT("questions.useQuestions.date", "Date"); },     icon: "calendar",group: "Special", opts: false, branch: false, value: true,  hint: "Date picker — a calendar day, no time." },
  { id: "file",     labelKey: "questions.useQuestions.fileUpload", get label() { return i18nT("questions.useQuestions.fileUpload", "File upload"); },     icon: "upload",  group: "Special", opts: false, branch: false, value: true,  hint: "Respondent uploads a file." },
  { id: "stepper",  labelKey: "questions.useQuestions.stepper", get label() { return i18nT("questions.useQuestions.stepper", "Stepper"); },     icon: "hash",    group: "Special", opts: false, branch: false, value: true,  hint: "Numeric −/+ picker; can hold several named counts." },
] as const;
/** Which question types own each per-type settings block — twin of the contract's SETTINGS_ALLOWED_TYPES. */
export const Q_SETTINGS_OWNERS: Record<string, string[]> = { textRules: ["short", "long", "number"], yesno: ["yesno"], select: ["select", "radio", "checks"], file: ["file"], stepper: ["stepper"], info: ["heading", "label"], number: ["number"], date: ["date", "datetime"] };
export const QTYPE = (id: string) => QFIELD_TYPES.find((t) => t.id === id) ?? QFIELD_TYPES[2];
/** The types an author can pick today — the catalog minus the retired ones. */
export const Q_PICKER_TYPES = QFIELD_TYPES.filter((t) => t.id !== "multi" && t.id !== "datetime");

let _optSeq = 0;
export const mkOpt = (label: string) => ({ id: `o_${Date.now().toString(36)}${(_optSeq++).toString(36)}`, label });

export interface Question {
  id: string; label: string; type: string; help: string; required: boolean;
  status: "approved" | "draft"; owner: string; updated: string;
  options: { id: string; label: string; allowsText?: boolean }[];
  /**
   * Answer rules for short/long text fields (validation preset or custom
   * regex, per-language error message, read-only, hidden-in-booking,
   * min/max length). Optional and absent on every other field type.
   *
   * ⚠️ NOT yet persisted: `QuestionDto` in the kit-owned api-contracts has no
   * field for it, so it survives in-session but is dropped on reload.
   * REQ-108 asks for the contract fields.
   */
  textRules?: QTextRules;
  placeholder?: string;
  defaultValue?: string;
  hiddenInBooking?: boolean;
  yesno?: QYesNoConfig;
  select?: QSelectConfig;
  file?: QFileConfig;
  stepper?: QStepperConfig;
  info?: QInfoConfig;
  number?: QNumberConfig;
  date?: QDateConfig;
}
/** Typed branch (contract QuestionBranchSchema): toggle state incl. unanswered, or a stepper row count. */
export type QBranchWhen =
  | { kind: "option"; optionId: string }
  | { kind: "toggle"; state: "on" | "off" | "unanswered" }
  | { kind: "stepper"; rowId: string; op: "eq" | "gte" | "lte" | "gt" | "lt"; count: number };
export interface QBranch { when: QBranchWhen; goTo: string; }
/** Show this item only when ANY condition holds, each on an earlier question (contract QuestionShowIfSchema). */
export interface QShowIf { qid: string; when: QBranchWhen; }
export interface GroupItem { qid: string; required?: boolean; rules?: Record<string, string>; branches?: QBranch[]; showIf?: QShowIf[]; }
export interface QuestionGroup {
  id: string; name: string; description: string; status: "approved" | "draft";
  owner: string; updated: string; items: GroupItem[];
}

function toQuestion(q: Record<string, any>): Question {
  return {
    id: q.id, label: q.label ?? q.question ?? "", type: q.type ?? q.fieldType ?? "short",
    help: q.help ?? q.helpText ?? "", required: q.required ?? false,
    // Read through under either name so the screen lights up the moment the
    // contract ships the field (REQ-108) with no further CD change.
    textRules: q.textRules ?? q.text_rules ?? undefined,
    // Same for the per-type editor config — read back if the server ever
    // returns it, absent otherwise.
    ...(q.placeholder !== undefined ? { placeholder: q.placeholder } : {}),
    ...(q.defaultValue !== undefined ? { defaultValue: q.defaultValue } : {}),
    ...(q.hiddenInBooking !== undefined ? { hiddenInBooking: q.hiddenInBooking } : {}),
    ...(q.yesno !== undefined ? { yesno: q.yesno } : {}),
    ...(q.select !== undefined ? { select: q.select } : {}),
    ...(q.file !== undefined ? { file: q.file } : {}),
    ...(q.stepper !== undefined ? { stepper: q.stepper } : {}),
    ...(q.info !== undefined ? { info: q.info } : {}),
    ...(q.number !== undefined ? { number: q.number } : {}),
    ...(q.date !== undefined ? { date: q.date } : {}),
    status: q.status === "approved" ? "approved" : "draft",
    owner: q.owner ?? q.createdBy ?? "—", updated: q.updatedAt ?? q.updated_at ?? "—",
    options: (q.options ?? []).map((o: any) => typeof o === "string" ? { id: o, label: o } : { id: o.id ?? o.value ?? o.label, label: o.label ?? o.value, ...(o.allowsText ? { allowsText: true } : {}) }),
  };
}
function toGroup(g: Record<string, any>): QuestionGroup {
  return {
    id: g.id, name: g.name ?? "", description: g.description ?? "",
    status: g.status === "approved" ? "approved" : "draft",
    owner: g.owner ?? g.createdBy ?? "—", updated: g.updatedAt ?? g.updated_at ?? "—",
    items: (g.items ?? g.questions ?? []).map((it: any) => ({
      qid: it.qid ?? it.questionId ?? it.id,
      required: it.required ?? undefined,
      rules: it.rules ?? undefined,
      branches: Array.isArray(it.branches) && it.branches.length ? it.branches : undefined,
      showIf: Array.isArray(it.showIf) && it.showIf.length ? it.showIf : undefined,
    })),
  };
}

let _qCache: Question[] | null = null;
let _gCache: QuestionGroup[] | null = null;
let _loadError: string | null = null;
const _listeners = new Set<() => void>();
const notify = () => _listeners.forEach((fn) => fn());
onTenantSwitching(() => { _qCache = null; _gCache = null; _loadError = null; notify(); });
onTenantChange(() => { if (can("questions.read")) void loadAll(); }); // admin/questions — gated (docket 2026-09-17)

/* ── Seed library (offline fallback / first-run demo data) ─────────────────
 * Two ready-made groups — food allergies & accessibility — with their
 * questions. Used when the contract returns an empty library so the screens
 * are never blank; a non-empty API response replaces it. */
async function loadAll() {
  const [qr, gr] = await Promise.all([listQuestions({ limit: 200 }), listQuestionGroups({ limit: 100 })])
    .catch((e) => { _loadError = e instanceof Error ? e.message : String(e); notify(); return [null, null] as const; });
  if (!qr || !gr) return;
  _loadError = null;
  const qArr = ((qr as any).questions ?? (qr as any).data ?? []).map(toQuestion);
  const gArr = ((gr as any).groups ?? (gr as any).data ?? []).map(toGroup);
  // Preserve anything created locally before hydration; render empty on a
  // genuinely-empty tenant. A FAILED read is NOT empty — it sets _loadError and
  // the screens render a visible error + retry (addendum item 31).
  const qLocal = (_qCache ?? []).filter((q) => !qArr.some((x: Question) => x.id === q.id));
  const gLocal = (_gCache ?? []).filter((g) => !gArr.some((x: QuestionGroup) => x.id === g.id));
  _qCache = qArr.length ? [...qLocal, ...qArr] : (_qCache ?? []);
  _gCache = gArr.length ? [...gLocal, ...gArr] : (_gCache ?? []);
  notify();
}

export function useQuestions() {
  const [, setV] = useState(0);

  useEffect(() => {
    const fn = () => setV((v) => v + 1);
    _listeners.add(fn);
    if (!_qCache) loadAll();
    return () => { _listeners.delete(fn); };
  }, []);

  let _seq = 0;
  const tmpId = (p: string) => `${p}_new_${Date.now().toString(36)}${(_seq++).toString(36)}`;

  return {
    /** True once the library read has answered (or failed). Until then a missing
     *  id means "not loaded yet", never "does not exist". */
    loaded: () => _qCache !== null || !!_loadError,
    questions: () => _qCache ?? [],
    loadError: () => _loadError,
    retryLoad: () => { _loadError = null; notify(); void loadAll(); },
    groups: () => _gCache ?? [],
    loading: _qCache === null,
    getQ: (id: string) => _qCache?.find((q) => q.id === id) ?? null,
    getGroup: (id: string) => _gCache?.find((g) => g.id === id) ?? null,
    usageOf: (qid: string) => (_gCache ?? []).filter((g) => g.items.some((it) => it.qid === qid)).length,
    newQuestion: (): Question => ({ id: tmpId("q"), label: "", type: "short", help: "", required: false, status: "draft", owner: "You", updated: "Just now", options: [] }),
    newGroup: (): QuestionGroup => ({ id: tmpId("g"), name: "", description: "", status: "draft", owner: "You", updated: "Just now", items: [] }),
    newOption: (label: string) => mkOpt(label),
    upsertQ: async (q: Question) => {
      const isNew = !_qCache?.some((x) => x.id === q.id);
      const prevQ = _qCache?.find((x) => x.id === q.id) ?? null;
      logUpsert("question", isNew ? null : (prevQ as unknown as Record<string, unknown>), q as unknown as Record<string, unknown>, { name: q.label });
      const opts = q.options.filter((o) => o.label.trim()).map((o) => (o.allowsText ? { id: o.id, label: o.label, allowsText: true } : { id: o.id, label: o.label }));
      // `textRules` is sent optimistically: the contract has no field for it yet
      // (REQ-108), so today the mock drops it and the rules do not survive a
      // reload. Sending it means the day the field lands, persistence works with
      // no CD change — and it is never sent as `{}`, so a question without rules
      // stays byte-identical to before.
      const rules = q.textRules && Object.keys(q.textRules).length ? { textRules: q.textRules } : {};
      // Same optimistic treatment for the per-type config the editor writes
      // (placeholder/prefill, hidden, toggle, dropdown, file, stepper, info).
      // None of it is in `QuestionDto` yet either — one REQ-108 covers the lot.
      // Each key is sent ONLY when present, so a plain question's payload is
      // byte-identical to before.
      const extra: Record<string, unknown> = {};
      for (const k of ["placeholder", "defaultValue", "hiddenInBooking", "yesno", "select", "file", "stepper", "info", "number", "date"] as const) {
        const v = q[k];
        if (v !== undefined) extra[k] = v;
      }
      /* Settings travel only with the type that owns them (server rule: a 'stepper'
         block on a short-text question is a 422). A type change leaves the old block
         in the form, so it is dropped here — and on an update cleared with null, since
         the server merges the patch into the stored row. Same table as the contract's
         SETTINGS_ALLOWED_TYPES. */
      const OWNS: Record<string, string[]> = Q_SETTINGS_OWNERS;
      const all: Record<string, unknown> = { ...rules, ...extra };
      for (const [k, types] of Object.entries(OWNS)) {
        if (types.includes(q.type)) continue;
        if (isNew || prevQ?.[k as keyof Question] === undefined) delete all[k];
        else all[k] = null;
      }
      // A hidden question is never required (operator, 2026-10-06; server rule).
      const required = q.hiddenInBooking ? false : q.required;
      const input = { label: q.label, type: q.type, help: q.help, required, status: q.status, options: opts, ...all };
      if (isNew) {
        const res = await createQuestion(input as any);
        _qCache = [...(_qCache ?? []), toQuestion((res as any).question ?? res)]; notify();
      } else {
        const res = await patchQuestion(q.id, input as any);
        const saved = toQuestion((res as any).question ?? res);
        _qCache = (_qCache ?? []).map((x) => x.id === q.id ? saved : x); notify();
      }
    },
    upsertGroup: async (g: QuestionGroup) => {
      const isNew = !_gCache?.some((x) => x.id === g.id);
      const prevG = _gCache?.find((x) => x.id === g.id) ?? null;
      logUpsert("questionGroup", isNew ? null : (prevG as unknown as Record<string, unknown>), g as unknown as Record<string, unknown>, { name: g.name });
      const input = { name: g.name, description: g.description, status: g.status, items: g.items };
      if (isNew) {
        const res = await createQuestionGroup(input as any);
        _gCache = [...(_gCache ?? []), toGroup((res as any).group ?? res)]; notify();
      } else {
        const res = await patchQuestionGroup(g.id, input as any);
        const saved = toGroup((res as any).group ?? res);
        _gCache = (_gCache ?? []).map((x) => x.id === g.id ? saved : x); notify();
      }
    },
    removeQ: async (id: string) => {
      const prev = (_qCache ?? []).find((x) => x.id === id);
      if (prev) logDelete("question", prev as unknown as Record<string, unknown>, { name: prev.label });
      await deleteQuestion(id);
      _qCache = (_qCache ?? []).filter((x) => x.id !== id); notify();
    },
    removeGroup: async (id: string) => {
      const prev = (_gCache ?? []).find((x) => x.id === id);
      if (prev) logDelete("questionGroup", prev as unknown as Record<string, unknown>, { name: prev.name });
      await deleteQuestionGroup(id);
      _gCache = (_gCache ?? []).filter((x) => x.id !== id); notify();
    },
    duplicateQ: async (id: string): Promise<Question> => {
      const src = _qCache?.find((x) => x.id === id);
      // Real typed payload (drop-33 docket §2): the duplicate facade takes a
      // QuestionDto — build it explicitly from the UI shape. `type` narrows from
      // the UI's plain string to the contract enum (values come from the QTYPE
      // catalog, which mirrors it); timestamps are required by the DTO but
      // unread by the facade.
      const nowIso = new Date().toISOString();
      if (!src) throw new Error(`question not found: ${id}`);
      const dto: QuestionDto = {
        id: src.id, label: src.label, type: src.type as QuestionDto["type"],
        help: src.help || undefined, required: src.required,
        options: src.options.map((o) => ({ id: o.id, label: o.label })),
        status: src.status, owner: src.owner || undefined,
        updatedAt: nowIso, createdAt: nowIso,
      };
      const res = await duplicateQuestion(dto);
      const copy = toQuestion((res as any).question ?? res);
      _qCache = [...(_qCache ?? []), copy]; notify();
      return copy;
    },
    duplicateGroup: async (id: string): Promise<QuestionGroup> => {
      const srcG = _gCache?.find((x) => x.id === id);
      const nowIso = new Date().toISOString();
      if (!srcG) throw new Error(`question group not found: ${id}`);
      const gDto: QuestionGroupDto = {
        id: srcG.id, name: srcG.name, description: srcG.description || undefined,
        status: srcG.status,
        items: srcG.items.map((it) => ({ qid: it.qid, required: it.required, rules: it.rules })),
        usedBy: 0, updatedAt: nowIso, createdAt: nowIso,
      };
      const res = await duplicateQuestionGroup(gDto);
      const copy = toGroup((res as any).group ?? res);
      _gCache = [...(_gCache ?? []), copy]; notify();
      return copy;
    },
    reload: () => { _qCache = null; _gCache = null; loadAll(); },
  };
}


/**
 * STAFF ON A CUSTOMER'S BEHALF (operator ruling 2026-10-06): hidden questions are
 * left off customer booking pages but shown to staff booking for someone in Studio.
 * Staff flows that run on the PUBLIC journey/queue reads (which correctly omit
 * hidden questions) call this to splice the hidden ones back in, in group order,
 * from the admin library the signed-in user already holds. A no-op for anyone
 * without questions.read — i.e. every customer — and before the library loads.
 * Limitation: a group whose questions are ALL hidden has no served row to carry its
 * groupId, so it can't be found from the public read alone.
 */
export function withStaffHiddenQuestions<TRow extends { id: string; groupId?: string | null }>(served: TRow[], groupIdHint?: string | null): TRow[] {
  if ((!served.length && !groupIdHint) || !can("questions.read") || !_qCache || !_gCache) return served;
  const have = new Set(served.map((r) => r.id));
  const out = [...served];
  // groupIdHint (2026-10-07): the service's group id from the public read, so an all-hidden group is still found.
  const groupIds = [...new Set([...served.map((r) => r.groupId), groupIdHint].filter((g): g is string => !!g))];
  for (const gid of groupIds) {
    const g = _gCache.find((x) => x.id === gid); if (!g) continue;
    let anchor = out.findIndex((r) => r.groupId === gid) - 1;
    if (anchor < -1) anchor = out.length - 1;
    for (const it of g.items) {
      const pos = out.findIndex((r) => r.id === it.qid);
      if (pos >= 0) { anchor = pos; continue; }
      const q = _qCache.find((x) => x.id === it.qid);
      if (!q || !q.hiddenInBooking || have.has(q.id)) continue;
      const row = { id: q.id, label: q.label, type: q.type, help: q.help, required: false, options: q.options, groupId: g.id, groupName: g.name, staffOnly: true,
        ...(q.placeholder ? { placeholder: q.placeholder } : {}), ...(q.defaultValue ? { defaultValue: q.defaultValue } : {}) } as unknown as TRow;
      out.splice(anchor + 1, 0, row); anchor += 1; have.add(q.id);
    }
  }
  return out;
}
